"use client";

import {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  ReactNode,
} from "react";

export interface Track {
  id: string;
  label: string;
  src: string;
}

export const TRACKS: Track[] = [
  { id: "rainy", label: "Rainy Vibes", src: "/music/rain.mp3" },
];

interface AudioPlayerContextValue {
  tracks: Track[];
  currentTrack: Track | null;
  isPlaying: boolean;
  isVisible: boolean;
  playCount: number;
  volume: number;
  play: (track: Track) => void;
  pause: () => void;
  resume: () => void;
  stop: () => void;
  setVolume: (v: number) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextValue | null>(null);

export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [currentTrack, setCurrentTrack] = useState<Track | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [playCount, setPlayCount] = useState(0);
  const [volume, setVolumeState] = useState(0.8);

  const play = useCallback((track: Track) => {
    if (!audioRef.current) {
      audioRef.current = new Audio(track.src);
      audioRef.current.loop = true;
    } else {
      audioRef.current.pause();
      audioRef.current.src = track.src;
      audioRef.current.load();
      audioRef.current.loop = true;
    }
    audioRef.current.volume = volume;
    audioRef.current.play().catch(() => {});
    setCurrentTrack(track);
    setIsPlaying(true);
    setIsVisible(true);
    setPlayCount((c) => c + 1);
  }, [volume]);

  const setVolume = useCallback((v: number) => {
    const clamped = Math.min(1, Math.max(0, v));
    if (audioRef.current) audioRef.current.volume = clamped;
    setVolumeState(clamped);
  }, []);

  const pause = useCallback(() => {
    audioRef.current?.pause();
    setIsPlaying(false);
  }, []);

  const resume = useCallback(() => {
    audioRef.current?.play().catch(() => {});
    setIsPlaying(true);
  }, []);

  const stop = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsPlaying(false);
    setIsVisible(false);
    setCurrentTrack(null);
  }, []);

  return (
    <AudioPlayerContext.Provider
      value={{ tracks: TRACKS, currentTrack, isPlaying, isVisible, playCount, volume, play, pause, resume, stop, setVolume }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}

export function useAudioPlayer() {
  const ctx = useContext(AudioPlayerContext);
  if (!ctx) throw new Error("useAudioPlayer must be inside AudioPlayerProvider");
  return ctx;
}
